import { useMemo } from 'react';
import { format } from 'date-fns';
import { useFetchSfQuery } from './salesforce-hooks';
import type { FiscalPeriodRow } from '../support/fiscalTimePeriods';
import { QuarterBounds, estimateQuarterBounds, quarterBounds } from '../support/fiscalQuarters';

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

/**
 * Current and next fiscal quarter and the fiscal year end, from the Salesforce `Period` calendar (cached).
 * Falls back to the locally computed calendar so deadlines and filters work when Salesforce can't be reached.
 */
export function useFiscalQuarters(): { bounds: QuarterBounds; loading: boolean } {
  const today = useMemo(() => new Date(), []);
  const day = format(today, 'yyyy-MM-dd');
  const query = useFetchSfQuery(
    `SELECT Type, StartDate, EndDate FROM Period WHERE Type IN ('Quarter', 'Year') AND EndDate >= ${day} ORDER BY StartDate LIMIT 8`,
    { staleTime: TWELVE_HOURS_MS, retry: 1 },
  );
  const bounds = useMemo((): QuarterBounds => {
    const rows: FiscalPeriodRow[] = (Array.isArray(query.data) ? query.data : [])
      .filter((r: any) => (r?.Type === 'Quarter' || r?.Type === 'Year') && r?.StartDate && r?.EndDate)
      .map((r: any) => ({ type: r.Type, start: String(r.StartDate), end: String(r.EndDate) }));
    const live = rows.length ? quarterBounds(rows, today) : null;
    return live?.current ? live : estimateQuarterBounds(today);
  }, [query.data, today]);
  return { bounds, loading: query.isLoading };
}
